import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { DashboardHeader, StatusBadge } from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { fmtDateLong, groupByDate, addMinutesToTime } from "../lib/dates";
import { CalendarClock, Clock, Loader2, CalendarX2, PawPrint, FileText, Download, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { ClinicStats } from "../components/ClinicStats";
import { openReport } from "../lib/download";
import { StudySearch } from "../components/StudySearch";
import { StudyMessages } from "../components/StudyMessages";
import { ClinicTariffs } from "../components/ClinicTariffs";
import { CancelBookingButton } from "../components/CancelBookingButton";
import { RescheduleBookingButton } from "../components/RescheduleBookingButton";
import { PatientDetails, fmtWeight } from "../components/PatientDetails";
import { filterStudies, EMPTY_STUDY_FILTER } from "../lib/studies";
import { ChevronDown, Paperclip, MessageSquare, Euro } from "lucide-react";

const SPECIES = ["Perro", "Gato",];
const STUDIES = ["Ecografía abdominal", "Ecocardiografía", "Ecografía de gestación", "Intervencionismo", "Otra"];

export default function ClinicDashboard() {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ patient_name: "", species: "", age: "", weight: "", study_type: "", reason: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ ...EMPTY_STUDY_FILTER });
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, b, m] = await Promise.all([api.get("/slots"), api.get("/bookings"), api.get("/auth/me")]);
      setSlots(s.data);
      setBookings(b.data);
      setMe(m.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openBooking = (slot) => {
    setSelected(slot);
    setForm({ patient_name: "", species: "", age: "", weight: "", study_type: "", reason: "", notes: "" });
  };

  const submitBooking = async () => {
    if (!form.patient_name || !form.species || !form.age || !form.weight || !form.study_type) {
      toast.error("Completa el paciente, la especie, la edad, el peso y el tipo de estudio");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { slot_id: selected.id, ...form });
      toast.success("Reserva solicitada. Pendiente de confirmación.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBooking = async (id, reason) => {
    try {
      await api.patch(`/bookings/${id}/cancel`, { reason });
      toast.success("Reserva cancelada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rescheduleBooking = async (id, slotId) => {
    try {
      await api.patch(`/bookings/${id}/reschedule`, { slot_id: slotId });
      toast.success("Cita reprogramada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const grouped = groupByDate(slots);
  const historyUnread = bookings.filter((b) => b.status === "completed").reduce((s, b) => s + (b.unread || 0), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <DashboardHeader subtitle="Panel de clínica" />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#373E45] mb-1">Reserva de ecografías</h1>
        <p className="text-[#64748B] mb-6">Consulta la disponibilidad y gestiona las citas de tu clínica.</p>

        <Tabs defaultValue="book">
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1">
            <TabsTrigger value="book" data-testid="tab-book" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Disponibilidad</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Mis reservas ({bookings.filter(b => b.status !== "cancelled").length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">
              Histórico
              {historyUnread > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold" data-testid="unread-badge-history">{historyUnread}</span>}
            </TabsTrigger>
            <TabsTrigger value="tariffs" data-testid="tab-tariffs" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Tarifas</TabsTrigger>
          </TabsList>

          <TabsContent value="book" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#7FD498]" /></div>
            ) : grouped.length === 0 ? (
              <Empty icon={CalendarX2} title="No hay franjas disponibles" desc="Por el momento no hay huecos libres. Vuelve a consultar más adelante." />
            ) : (
              <div className="space-y-6" data-testid="available-slots">
                {grouped.map(({ date, slots }) => (
                  <div key={date} className="bg-white rounded-2xl border border-slate-200 p-6">
                    <h3 className="font-bold text-[#373E45] capitalize flex items-center gap-2 mb-4">
                      <CalendarClock className="h-5 w-5 text-[#7FD498]" />{fmtDateLong(date)}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {slots.map((s) => (
                        <button key={s.id} onClick={() => openBooking(s)} data-testid={`slot-${s.id}`}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#7FD498] bg-[#7FD498]/10 text-[#373E45] font-bold text-sm hover:bg-[#7FD498] transition-colors">
                          <Clock className="h-4 w-4" />{s.start_time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine" className="mt-6">
            {bookings.length === 0 ? (
              <Empty icon={PawPrint} title="Aún no tienes reservas" desc="Reserva tu primera ecografía desde la pestaña de disponibilidad." />
            ) : (
              <>
                <ClinicStats bookings={bookings} />
                <div className="space-y-3" data-testid="my-bookings">
                {bookings.map((b) => (
                  <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`booking-${b.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-bold text-[#373E45] capitalize">{fmtDateLong(b.slot_date)}</span>
                        <span className="text-[#64748B] text-sm">{b.slot_start} – {b.slot_end}</span>
                        <StatusBadge status={b.status} />
                      </div>
                      <p className="text-sm text-[#64748B]">
                        <span className="font-semibold text-[#373E45]">{b.patient_name}</span> · {b.species}{b.age ? ` · ${b.age}` : ""}{b.weight ? ` · ${fmtWeight(b.weight)}` : ""} · {b.study_type}
                        {b.reason ? ` · ${b.reason}` : ""}
                      </p>
                    </div>
                    {(b.status === "pending" || b.status === "confirmed") && (
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <RescheduleBookingButton testid={`reschedule-booking-${b.id}`} slots={slots}
                          current={`${fmtDateLong(b.slot_date)} · ${b.slot_start}`}
                          onConfirm={(slotId) => rescheduleBooking(b.id, slotId)} />
                        <CancelBookingButton testid={`cancel-booking-${b.id}`} label="Cancelar" onConfirm={(reason) => cancelBooking(b.id, reason)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            {(() => {
              const done = bookings.filter((b) => b.status === "completed");
              const filtered = filterStudies(done, historyFilter);
              return done.length === 0 ? (
                <Empty icon={FileText} title="Aún no hay ecografías completadas" desc="Aquí verás las ecografías realizadas, con las notas del veterinario y el informe en PDF." />
              ) : (
                <div data-testid="history-list">
                  <StudySearch filter={historyFilter} setFilter={setHistoryFilter} resultCount={filtered.length} />
                  {filtered.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="history-no-results">No hay estudios que coincidan con la búsqueda.</p> : (
                  <div className="space-y-3">
                  {[...filtered].sort((a, b) => (b.slot_date + b.slot_start).localeCompare(a.slot_date + a.slot_start)).map((b) => (
                    <ClinicHistoryRow key={b.id} b={b} onReload={load} />
                  ))}
                  </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="tariffs" className="mt-6">
            <ClinicTariffs me={me} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#373E45]">Reservar ecografía</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="bg-[#7FD498]/10 border border-[#7FD498] rounded-xl p-3 text-sm font-semibold text-[#373E45] capitalize">
                {fmtDateLong(selected.date)} · {selected.start_time} – {addMinutesToTime(selected.start_time, 90)}
                <span className="block text-xs font-normal text-[#64748B] mt-0.5 normal-case">Duración de la cita: 1 h 30 min</span>
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Nombre del paciente</Label>
                <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Ej. Luna" data-testid="booking-patient-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#373E45] font-semibold">Edad</Label>
                  <Input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Ej. 3 años" data-testid="booking-age-input" />
                </div>
                <div>
                  <Label className="text-[#373E45] font-semibold">Peso</Label>
                  <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Ej. 12 kg" data-testid="booking-weight-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#373E45] font-semibold">Especie</Label>
                  <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                    <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="booking-species-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{SPECIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#373E45] font-semibold">Tipo de estudio</Label>
                  <Select value={form.study_type} onValueChange={(v) => setForm({ ...form, study_type: v })}>
                    <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="booking-study-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{STUDIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Motivo / sospecha clínica</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Opcional" data-testid="booking-reason-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Notas adicionales</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" placeholder="Opcional" data-testid="booking-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitBooking} disabled={submitting} className="w-full bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="confirm-booking-btn">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Solicitar reserva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Empty({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-16 text-center">
      <Icon className="h-10 w-10 text-slate-300 mx-auto mb-4" />
      <h3 className="font-bold text-[#373E45]">{title}</h3>
      <p className="text-sm text-[#64748B] mt-1">{desc}</p>
    </div>
  );
}


function ClinicHistoryRow({ b, onReload }) {
  const [open, setOpen] = useState(false);
  const msgCount = (b.messages || []).length;
  const unread = b.unread || 0;
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) { try { await api.patch(`/bookings/${b.id}/read`); onReload(); } catch (e) { /* noop */ } }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`history-${b.id}`}>
      <button onClick={toggle} className="w-full text-left p-5 hover:bg-slate-50 transition-colors" data-testid={`history-toggle-${b.id}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {b.case_number && <span className="text-xs font-extrabold text-white bg-[#373E45] rounded-md px-2 py-0.5 tracking-widest" data-testid={`history-case-${b.id}`}>{b.case_number}</span>}
              <span className="font-bold text-[#373E45] capitalize">{fmtDateLong(b.slot_date)}</span>
              <span className="text-sm text-[#64748B]">{b.slot_start}–{b.slot_end}</span>
              {b.report_path && <Paperclip className="h-3.5 w-3.5 text-[#7FD498]" />}
              {msgCount > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold text-[#5a8a68]"><MessageSquare className="h-3.5 w-3.5" />{msgCount}</span>}
              {unread > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold" data-testid={`history-unread-${b.id}`}>{unread}</span>}
            </div>
            <p className="text-sm text-[#64748B] mt-1 truncate"><span className="font-semibold text-[#373E45]">{b.patient_name}</span> · {b.species} · {b.study_type}</p>
          </div>
          <ChevronDown className={`h-5 w-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100" data-testid={`history-detail-${b.id}`}>
          <PatientDetails b={b} />
          <div className="mt-3">
            {b.report_path ? (
              <Button size="sm" onClick={() => openReport(b.id)} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid={`download-${b.id}`}>
                <Download className="h-4 w-4 mr-1" />Descargar informe PDF
              </Button>
            ) : (
              <p className="text-sm text-slate-400 flex items-center gap-1"><FileText className="h-4 w-4" />Informe aún no disponible</p>
            )}
          </div>
          <StudyMessages b={b} onReload={onReload} meRole="clinic" />
        </div>
      )}
    </div>
  );
}
